import { AloneTelemetry } from 'common/services/monitoring/telemetry/natteringTelemetry';

export type AloneContext = 'pair' | 'group';
export type RecoveredVia = 'partner_returned' | 'rematched';
export type UnresolvedEnding = 'round_ended' | 'user_left';

/** Round identity used to tag events and enforce same-round recovery. */
export type AloneRound = { id: number | undefined; startTime: number | undefined };

export type AloneTelemetrySink = (action: `nattering.alone.${string}`, extra?: Record<string, unknown>) => void;

export type AloneRecoveryTrackerDeps = {
  /** Emits the event — in production this is `monitoring.trackAction`. */
  track: AloneTelemetrySink;
  /** Wall clock, injectable for tests. */
  now: () => number;
  /** Unique id per alone episode, injectable for tests. */
  newEpisodeId: () => string;
};

type Episode = {
  id: string;
  roundId: number | undefined;
  roundStartTime: number | undefined;
  context: AloneContext;
  startedAt: number;
  viaRematch: boolean;
  hostSeen: boolean;
};

export type AloneRecoveryTracker = {
  /** Peer-partner presence changed (true = a working peer is present). */
  recordPresence: (present: boolean, context: AloneContext, round: AloneRound) => void;
  /** Host presence changed; emits host_present once while an episode is open. */
  recordHostPresence: (present: boolean, round: AloneRound) => void;
  /** A confirmed re-pair happened — the eventual recovery is `rematched`. */
  recordRematchConfirmed: () => void;
  /** Round ended with the user still alone. */
  recordRoundEnded: () => void;
  /** User explicitly left the event while alone; latches against further `began`. */
  recordUserLeft: () => void;
  /** Clear open episode + left latch (new event/session). */
  reset: () => void;
};

export const createAloneRecoveryTracker = ({
  track,
  now,
  newEpisodeId,
}: AloneRecoveryTrackerDeps): AloneRecoveryTracker => {
  let episode: Episode | null = null;
  let leftLatch = false;

  const roundAttrs = (ep: Episode) => ({
    episodeId: ep.id,
    roundId: ep.roundId,
    roundStartTime: ep.roundStartTime,
  });

  const finalizeUnresolved = (ending: UnresolvedEnding): void => {
    if (!episode) return;
    track(AloneTelemetry.unresolved, {
      ...roundAttrs(episode),
      msAlone: now() - episode.startedAt,
      ending,
      hostPresent: episode.hostSeen,
    });
    episode = null;
  };

  const recordPresence = (present: boolean, context: AloneContext, round: AloneRound): void => {
    if (!present) {
      if (leftLatch || episode) return;
      episode = {
        id: newEpisodeId(),
        roundId: round.id,
        roundStartTime: round.startTime,
        context,
        startedAt: now(),
        viaRematch: false,
        hostSeen: false,
      };
      track(AloneTelemetry.began, { ...roundAttrs(episode), context });
      return;
    }

    if (!episode) return;

    // A return in a different round is not a recovery of this episode; close it as round_ended.
    if (round.id !== undefined && episode.roundId !== undefined && round.id !== episode.roundId) {
      finalizeUnresolved('round_ended');
      return;
    }

    track(AloneTelemetry.recovered, {
      ...roundAttrs(episode),
      msAlone: now() - episode.startedAt,
      via: episode.viaRematch ? 'rematched' : 'partner_returned',
      hostPresent: episode.hostSeen,
    });
    episode = null;
  };

  const recordHostPresence = (present: boolean, _round: AloneRound): void => {
    if (!episode || !present || episode.hostSeen) return;
    episode.hostSeen = true;
    track(AloneTelemetry.hostPresent, {
      ...roundAttrs(episode),
      msAlone: now() - episode.startedAt,
    });
  };

  const recordRematchConfirmed = (): void => {
    if (episode) episode.viaRematch = true;
  };

  const recordRoundEnded = (): void => finalizeUnresolved('round_ended');

  const recordUserLeft = (): void => {
    leftLatch = true;
    finalizeUnresolved('user_left');
  };

  const reset = (): void => {
    episode = null;
    leftLatch = false;
  };

  return {
    recordPresence,
    recordHostPresence,
    recordRematchConfirmed,
    recordRoundEnded,
    recordUserLeft,
    reset,
  };
};
